// Pagador da UC: quem paga o boleto, separado do cliente (migration 20261013180000).
//
// As UCs de Morrinhos têm o cliente "Prefeitura", mas quem paga é o Fundo de cada
// Secretaria. A UC aponta para um pagador (unidades_consumidoras.pagador_id), e o
// mesmo pagador serve várias UCs.
//
// Ordem no boleto: acordo → PAGADOR DA UC → assinante do termo → cadastro da UC →
// cálculo anterior (ver escolherPagador em pagadorBoleto.ts).
//
// Sem imports de runtime: roda sob `node --test`.

export interface PagadorUc {
    id: string;
    nome: string;
    /** Só dígitos; nulo nos que vieram da carga sem documento. */
    cpf_cnpj: string | null;
    email: string | null;
    whatsapp: string | null;
    /** O documento aparece com nomes de pessoas diferentes no cadastro das UCs. */
    conferir_documento: boolean;
}

export interface FontePagadorUc {
    origem: string;
    nome: string;
    documento: string | null;
}

export const ORIGEM_PAGADOR_UC = 'pagador da UC';

/**
 * O pagador da UC como fonte do boleto, ou null quando não vale para ESTA fatura.
 *
 * Não vale quando a fatura é de troca de dono (`trocaNoCaminho`): com o pedido
 * aberto ou com a fatura carimbada do dono anterior, quem paga é o dono anterior,
 * e o pagador da UC já pode ser o do dono novo.
 */
export function fontePagadorDaUc(
    pagador: Pick<PagadorUc, 'nome' | 'cpf_cnpj'> | null | undefined,
    trocaNoCaminho: boolean,
): FontePagadorUc | null {
    if (trocaNoCaminho || !pagador) return null;
    const nome = (pagador.nome || '').trim();
    if (!nome) return null;
    return { origem: ORIGEM_PAGADOR_UC, nome, documento: pagador.cpf_cnpj ?? null };
}

/** CPF/CNPJ só com dígitos, ou null se não tiver 11 nem 14 — mesma regra do banco (_uc_titular_doc). */
export function documentoDoPagador(v: string | null | undefined): string | null {
    const d = String(v ?? '').replace(/\D/g, '');
    return d.length === 11 || d.length === 14 ? d : null;
}

/** CPF ou CNPJ com dígitos verificadores certos. */
export function documentoPagadorValido(v: string | null | undefined): boolean {
    const d = documentoDoPagador(v);
    if (!d || /^(\d)\1+$/.test(d)) return false;
    if (d.length === 11) {
        for (const n of [9, 10]) {
            let soma = 0;
            for (let i = 0; i < n; i++) soma += Number(d[i]) * (n + 1 - i);
            if (((soma * 10) % 11) % 10 !== Number(d[n])) return false;
        }
        return true;
    }
    const pesos = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    for (const n of [12, 13]) {
        let soma = 0;
        for (let i = 0; i < n; i++) soma += Number(d[i]) * pesos[i + 13 - n];
        const dv = 11 - (soma % 11);
        if ((dv >= 10 ? 0 : dv) !== Number(d[n])) return false;
    }
    return true;
}

/**
 * Erro da pergunta "O assinante também será o pagador?" ou null se está ok.
 * `obrigatoria`: na negociação que já cadastra UC a resposta é exigida.
 */
export function erroPerguntaPagador(
    d: { pagador_mesmo?: boolean | null; pagador_nome?: string; pagador_cpf_cnpj?: string },
    obrigatoria: boolean,
): string | null {
    if (d.pagador_mesmo === null || d.pagador_mesmo === undefined) {
        return obrigatoria ? 'Responda se o assinante também será o pagador do boleto.' : null;
    }
    if (d.pagador_mesmo) return null;
    if (!(d.pagador_nome || '').trim()) return 'Informe o nome de quem vai pagar o boleto.';
    if (!documentoPagadorValido(d.pagador_cpf_cnpj)) return 'CPF/CNPJ do pagador inválido.';
    return null;
}

/** Aviso antes de editar um pagador que serve várias UCs. */
export function avisoPagadorCompartilhado(totalUcs: number): string | null {
    if (totalUcs <= 1) return null;
    return `Este pagador está em ${totalUcs} UCs. Mudar o nome ou o CPF/CNPJ aqui muda o boleto de todas elas. Para mudar só esta UC, vincule outro pagador.`;
}

/** Só os dígitos do telefone, sem o 55 do Brasil — para comparar dois números. */
function digitosFone(v: string | null | undefined): string {
    const d = String(v ?? '').replace(/\D/g, '');
    return d.length > 11 && d.startsWith('55') ? d.slice(2) : d;
}

/**
 * O financeiro já gravado é outra pessoa? (#6384) Conta nome, e-mail ou WhatsApp
 * próprios — os antigos vinham copiados do cliente e não contam. Usado para abrir
 * o termo com a resposta marcada, sem apagar o financeiro de quem já tinha um.
 */
export function financeiroEhOutraPessoa(n: {
    cliente_nome?: string | null; email?: string | null; whatsapp?: string | null;
    contato_financeiro?: string | null; email_financeiro?: string | null; whatsapp_financeiro?: string | null;
}): boolean {
    const txt = (v?: string | null) => (v || '').trim().toLowerCase();
    const nome = txt(n.contato_financeiro);
    const email = txt(n.email_financeiro);
    const fone = digitosFone(n.whatsapp_financeiro);
    return (!!nome && nome !== txt(n.cliente_nome))
        || (!!email && email !== txt(n.email))
        || (!!fone && fone !== digitosFone(n.whatsapp));
}

/**
 * Erro dos contatos exigidos no termo de adesão (#6384) ou null se está ok.
 *
 * O WhatsApp e o e-mail do associado são obrigatórios (o gerador do termo exige
 * os dois), e o vendedor responde se o contato
 * financeiro é outra pessoa. Se for, nome e WhatsApp dela são obrigatórios e o
 * número não pode ser o mesmo do associado: era assim que o vendedor deixava só
 * um contato. O contato financeiro não muda em nome de quem sai o boleto.
 */
export function erroContatosDoTermo(d: {
    whatsapp?: string | null;
    email?: string | null;
    financeiro_outro?: boolean | null;
    contato_financeiro?: string | null;
    whatsapp_financeiro?: string | null;
}): string | null {
    const foneAssociado = digitosFone(d.whatsapp);
    if (foneAssociado.length < 10) return 'Informe o WhatsApp do associado.';
    if (!(d.email || '').trim()) return 'Informe o e-mail do associado.';
    if (d.financeiro_outro === null || d.financeiro_outro === undefined) return 'Responda se o contato financeiro é outra pessoa.';
    if (!d.financeiro_outro) return null;
    if (!(d.contato_financeiro || '').trim()) return 'Informe o nome do contato financeiro.';
    const foneFinanceiro = digitosFone(d.whatsapp_financeiro);
    if (foneFinanceiro.length < 10) return 'Informe o WhatsApp do contato financeiro.';
    if (foneFinanceiro === foneAssociado) return 'O WhatsApp do financeiro não pode ser o mesmo do associado.';
    return null;
}

const palavrasDoNome = (v: string | null | undefined): string[] =>
    String(v ?? '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase()
        .replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);

/**
 * #6565: o pagador tem o CPF/CNPJ do cliente, mas o nome de outra pessoa.
 *
 * Acontece quando trocam o pagador antes do cliente: o formulário do pagador vem
 * preenchido com o nome do cliente daquele momento, a pessoa troca só o CPF e,
 * depois, troca o cliente. O pagador não acompanha, e o boleto sai com o CPF novo
 * e o nome antigo. Nome só abreviado ou completo ("Fernando Tavares" e "Fernando
 * Tavares Bertoldo") não conta: todas as palavras do mais curto estão no outro.
 */
export function pagadorComOutroNome(
    pagador: Pick<PagadorUc, 'nome' | 'cpf_cnpj'> | null | undefined,
    nomeCliente: string | null | undefined,
    cpfCliente: string | null | undefined,
): boolean {
    if (!pagador) return false;
    const docPagador = documentoDoPagador(pagador.cpf_cnpj);
    if (!docPagador || docPagador !== documentoDoPagador(cpfCliente)) return false;
    const a = palavrasDoNome(pagador.nome);
    const b = palavrasDoNome(nomeCliente);
    if (a.length === 0 || b.length === 0) return false;
    const [curto, longo] = a.length <= b.length ? [a, b] : [b, a];
    return !curto.every(p => longo.includes(p));
}
